<template>
    <section class="app-showcase band-night">
        <div class="section-head">
            <span class="kicker">{{ copy.kicker }}</span>
            <h2>{{ copy.title }}</h2>
        </div>
        <div
            class="phones"
            data-aos="fade-up"
            @touchstart.passive="onTouchStart"
            @touchend.passive="onTouchEnd"
        >
            <figure
                v-for="(slide, index) in copy.slides"
                v-bind:key="slide.img"
                class="phone"
                :class="[`phone--${index}`, { 'phone--active': index === active }]"
            >
                <div class="phone__frame">
                    <img
                        :src="slide.img"
                        :alt="slide.caption"
                        loading="lazy"
                        decoding="async"
                    />
                </div>
                <figcaption>{{ slide.caption }}</figcaption>
            </figure>
        </div>
        <div
            class="dots"
            aria-hidden="true"
        >
            <span
                v-for="(slide, index) in copy.slides"
                v-bind:key="slide.img"
                :class="{ 'dots__dot--active': index === active }"
                class="dots__dot"
                @click="goTo(index)"
            ></span>
        </div>
        <div
            v-if="buttons?.length"
            class="store-badges app-showcase__badges"
        >
            <Link
                v-for="button in buttons"
                :to="button.url"
                :label="button.label"
                :image="button.image"
                v-bind:key="button.url"
            />
        </div>
    </section>
</template>

<script>
import { mapState } from 'pinia'
import { useStore } from '@/stores/index'
import { getHomeContent } from '@/data/home'
import Link from '@/components/Links/Link.vue'

export default {
    name: 'AppShowcaseSection',
    components: { Link },
    props: {
        buttons: Array,
    },
    data() {
        return {
            active: 0,
            timer: null,
            touchX: 0,
        }
    },
    mounted() {
        this.startTimer()
    },
    beforeUnmount() {
        window.clearInterval(this.timer)
    },
    methods: {
        startTimer() {
            window.clearInterval(this.timer)
            this.timer = window.setInterval(() => this.step(1), 3000)
        },
        step(delta) {
            const count = this.copy.slides.length
            this.active = (this.active + delta + count) % count
        },
        goTo(index) {
            this.active = index
            this.startTimer()
        },
        onTouchStart(e) {
            this.touchX = e.changedTouches[0].clientX
        },
        onTouchEnd(e) {
            const dx = e.changedTouches[0].clientX - this.touchX
            if (Math.abs(dx) < 30) return
            this.step(dx < 0 ? 1 : -1)
            this.startTimer()
        },
    },
    computed: {
        ...mapState(useStore, ['language']),
        copy() {
            return getHomeContent(this.language).showcase
        },
    },
}
</script>

<style lang="scss" scoped>
.app-showcase {
    overflow: hidden;
    background-image:
        radial-gradient(ellipse 50% 40% at 50% 100%, rgba(218, 138, 139, 0.25), transparent 70%);
}

.phones {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 28px;
    max-width: 1000px;
    margin: 0 auto;
}

.phone {
    margin: 0;
    flex: 0 1 260px;
    min-width: 0;
    text-align: center;
}

.phone__frame {
    border-radius: 28px;
    overflow: hidden;
    background: #000;
    box-shadow:
        0 30px 60px rgba(0, 0, 0, 0.55),
        0 0 0 1px rgba(255, 255, 255, 0.08);
    transition: transform 320ms cubic-bezier(0.2, 0.8, 0.3, 1);
}

.phone__frame img {
    display: block;
    width: 100%;
    height: auto;
}

.phone:hover .phone__frame {
    transform: translateY(-8px);
}

/* The middle phone leads. Side phones sit a little lower and lean in. */
.phone--0 .phone__frame { transform: rotate(-3deg); }
.phone--2 .phone__frame { transform: rotate(3deg); }
.phone--1 { flex-basis: 290px; margin-bottom: 24px; }
.phone--0:hover .phone__frame { transform: rotate(-3deg) translateY(-8px); }
.phone--2:hover .phone__frame { transform: rotate(3deg) translateY(-8px); }

figcaption {
    margin-top: 22px;
    font-size: 15px;
    font-weight: 600;
    opacity: 0.85;
}

.app-showcase__badges {
    margin-top: 48px;
}

.dots {
    display: none;
    justify-content: center;
    gap: 8px;
    margin-top: 22px;
}

.dots__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.25);
    transition: background-color 300ms ease, transform 300ms ease;
}

.dots__dot--active {
    background: var(--bzioup-color-pink);
    transform: scale(1.25);
}

/* Phones: the three screens stack in one cell and auto-rotate. */
@media (max-width: $mq-medium) {
    .phones {
        display: grid;
        justify-items: center;
        touch-action: pan-y;
    }

    .phone,
    .phone--1 {
        grid-area: 1 / 1;
        width: 100%;
        max-width: 260px;
        margin: 0;
        opacity: 0;
        transform: translateX(40px);
        transition: opacity 500ms ease, transform 500ms cubic-bezier(0.2, 0.8, 0.3, 1);
        pointer-events: none;
    }

    .phone--active {
        opacity: 1;
        transform: none;
        pointer-events: auto;
    }

    .phone .phone__frame {
        transform: none;
    }

    .dots {
        display: flex;
    }
}
</style>
